import React from 'react';
import { Brain, Calculator, BookOpen, Briefcase, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HomePage = ({ 
  currentProblem, 
  userAnswer, 
  setUserAnswer, 
  checkDemoAnswer, 
  feedback, 
  score, 
  problemsAttempted, 
  setCurrentPage,
  goToLeaderboard,
  mobileMenuOpen,
  setMobileMenuOpen
}) => {
  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Mental Math Trainer",
      description: "Calculate faster than your calculator. Master the mental math that powers split-second financial decisions."
    },
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Sequences Trainer",
      description: "Spot patterns instantly. Master the sequence recognition that helps you read market movements and data trends."
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "The Prep Blog",
      description: "Your competitive advantage. Discover interview tactics and market knowledge that set top candidates apart."
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Job Opportunities",
      description: "Exclusive access to trading internships and job postings from our network of partner firms."
    }
  ];

  const stats = [
    { value: "500+", label: "Interview Questions" },
    { value: "100%", label: "Expert-Created content" },
    { value: "5+", label: "Industry Partners" },
    { value: "99%+", label: "Success Rate" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 overflow-x-hidden">
      <Header 
        setCurrentPage={setCurrentPage} 
        goToLeaderboard={goToLeaderboard}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Hero Section */}
      <section className="pt-24 pb-16 text-white px-4">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-6">
            Master Your Trading Interview
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            Accelerate your mental math, sharpen your trading instincts, and dominate interviews at top quantitative trading firms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setCurrentPage('practice')}
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
            >
              Start Training
            </button>
          </div>
        </div>
      </section>

      {/* Quick Demo Section */}
      <section id="demo" className="py-16 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Try Our Mental Math Trainer
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6">
                Get a taste of our training with this quick demo. The full tests have more questions and detailed analytics.
              </p>
              <div className="flex items-center gap-4 sm:gap-6 mb-6 flex-wrap">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{score}</div>
                  <div className="text-sm text-gray-500">Correct</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{problemsAttempted}</div>
                  <div className="text-sm text-gray-500">Attempted</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {problemsAttempted > 0 ? Math.round((score / problemsAttempted) * 100) : 0}%
                  </div>
                  <div className="text-sm text-gray-500">Accuracy</div>
                </div>
              </div>
              <button 
                onClick={() => setCurrentPage('practice')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center gap-2 text-sm sm:text-base"
              >
                Take Full Test
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl">
              <div className="text-center">
                {currentProblem ? (
                  <>
                    <div className="mb-6">
                      <div className="text-base sm:text-lg font-medium text-gray-800 mb-4">
                        {currentProblem.question}
                      </div>
                      <input
                        type="number"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && checkDemoAnswer()}
                        placeholder="Enter your answer"
                        className="w-full p-3 text-base sm:text-lg border-2 border-gray-200 rounded-xl text-center focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={checkDemoAnswer}
                      disabled={!userAnswer}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      Check Answer
                    </button>
                    {feedback && (
                      <div className={`mt-4 p-3 rounded-xl font-semibold text-sm ${
                        feedback.correct 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {feedback.message}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="py-12 text-gray-500">
                    Loading demo...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive preparation tools designed specifically for trading and quant interviews
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
              Trusted by Future Traders
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm sm:text-base text-gray-600 font-semibold">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
            Ready for the Challenge?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Join us to build your edge and land offers at top financial institutions.
          </p>
          <button 
            onClick={() => setCurrentPage('practice')}
            className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
          >
            Start Your Journey
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <Footer setCurrentPage={setCurrentPage} goToLeaderboard={goToLeaderboard} />
    </div>
  );
};

export default HomePage;